// One way to show loading, empty, error-with-retry and offline (FR-040; session-seam §6; research R15;
// constitution VIII). The error message is chosen by AppError.kind and code; the request ID is shown
// as a support reference. Never the server's detail text.
import { StyleSheet, View } from "react-native";

import type { AppError, AppErrorKind } from "@/api/errors";
import { makeStyles } from "@/theme/makeStyles";
import { BrandLoader } from "@/ui/BrandLoader";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

export type StateViewProps =
  | { status: "loading" }
  | { status: "empty"; title: string; message?: string }
  | { status: "error"; error: AppError; onRetry: () => void }
  | { status: "offline"; onRetry?: () => void };

const MESSAGE_BY_KIND: Record<AppErrorKind, string> = {
  offline: "You're offline. Check your connection and try again.",
  timeout: "This is taking longer than it should. Try again.",
  unauthenticated: "You need to log in again to see this.",
  // 002 FR-046: the role or school no longer allows it; the session is renewed meanwhile.
  forbidden: "You don't have access to this any more",
  notFound: "We couldn't find what you were looking for.",
  validation: "Something in the request wasn't accepted. Try again.",
  rateLimited: "Too many tries in a short time. Wait a moment and try again.",
  unavailable:
    "Akaiva Edu Power is briefly unavailable. Try again in a few minutes.",
  server: "Something went wrong on our side. Try again.",
  conflict: "This changed while you were working. Reload and try again.",
  unknown: "Something went wrong on our side. Try again.",
};

const MESSAGE_BY_CODE: Readonly<Record<string, string>> = {
  SESSION_ENDED: "Your session has ended. Log in again to continue.",
};

export function errorMessage(error: AppError): string {
  return (
    (error.code && MESSAGE_BY_CODE[error.code]) ?? MESSAGE_BY_KIND[error.kind]
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    root: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: t.space.x2l,
      gap: t.space.md,
    },
    message: { textAlign: "center", maxWidth: 480 },
  }),
);

export function Reference({ requestId }: { requestId: string | null }) {
  if (!requestId) return null;
  return (
    <Text size="xs" tone="muted" selectable>
      {`Reference: ${requestId}`}
    </Text>
  );
}

export function StateView(props: StateViewProps) {
  const styles = useStyles();
  switch (props.status) {
    case "loading":
      return (
        <View style={styles.root} testID="state-loading">
          <BrandLoader />
        </View>
      );
    case "empty":
      return (
        <View style={styles.root} testID="state-empty">
          <Text variant="heading" size="lg" accessibilityRole="header">
            {props.title}
          </Text>
          {props.message ? (
            <Text tone="muted" style={styles.message}>
              {props.message}
            </Text>
          ) : null}
        </View>
      );
    case "error":
      return (
        <View style={styles.root} testID="state-error" role="alert">
          <Text style={styles.message}>{errorMessage(props.error)}</Text>
          <PressableTarget onPress={props.onRetry}>Retry</PressableTarget>
          <Reference requestId={props.error.requestId} />
        </View>
      );
    case "offline":
      return (
        <View style={styles.root} testID="state-offline">
          <Text style={styles.message}>{MESSAGE_BY_KIND.offline}</Text>
          {props.onRetry ? (
            <PressableTarget onPress={props.onRetry}>Retry</PressableTarget>
          ) : null}
        </View>
      );
  }
}
